import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  Skeleton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  keyframes,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const API_BASE = process.env.REACT_APP_API_BASE || "https://panel.haymestasarim.com/api";

// ✅ İlk sipariş / ilk garson çağrısı geç düşmesin diye hızlı kontrol
// 2500 ms = 2.5 saniye. Backend’i yormadan hızlı algılar.
const ORDER_POLL_INTERVAL_MS = 2500;
const WAITER_POLL_INTERVAL_MS = 2500;

const ORDER_TEXT = {
  tr: {
pageTitle: "Sipariş Yönetimi",
    pageSubtitle: "QR menü siparişlerini, garson çağrılarını, stok ve ciro takibini tek ekrandan yönetin.",
    soundOn: "Sesli uyarı açık",
    soundOff: "Sesli uyarı kapalı",
    waiterCalls: "Garson Çağrıları",
    refreshOrders: "Siparişleri yenile",
    searchPlaceholder: "Sipariş ID, müşteri adı veya telefon ara",
    all: "Tümü",
    new: "Yeni",
    preparing: "Hazırlanıyor",
    completed: "Tamamlandı",
    cancelled: "İptal",
    table: "Masa",
    customer: "Müşteri",
    phone: "Telefon",
    note: "Not",
    total: "Toplam",
    markPreparing: "Hazırlanıyor",
    markCompleted: "Tamamlandı",
    cancel: "İptal",
    criticalStock: "Kritik Stok",
    criticalStockSub: "Ürün kritik seviyede",
    dailyRevenue: "Günlük Ciro",
    totalOrders: "Toplam Sipariş",
    period: "Periyot",
    daily: "Günlük",
    monthly: "Aylık",
    topProducts: "En Çok Satan Ürünler",
    noSalesData: "Henüz satış verisi yok",
    piece: "adet",
    stockManagement: "Stok Yönetimi",
    stockHelp: "İşletme ürün stoklarını buradan girer. Örn: Ayran 100 adet, minimum 10 adet.",
    addStock: "Stok Ekle",
    emptyStock: "Henüz stok girilmemiş. Stok Ekle butonuna basarak ürün adetlerini girebilirsiniz.",
    productName: "Ürün adı",
    stockQty: "Stok Adedi",
    minWarning: "Minimum Uyarı",
    critical: "Kritik / Tükendi",
    stockNormal: "Stok Normal",
    save: "Kaydet",
    delete: "Sil",
    revenueTrend: "Ciro Trendi",
    periodCol: "Dönem",
    orderCol: "Sipariş",
    revenueCol: "Ciro",
    noFiltered: "Filtrelerinizle eşleşen sipariş bulunamadı.",
    noOrders: "Henüz sipariş yok.",
    noWaiter: "Henüz garson çağrısı yok",
    tableUnspecified: "Belirtilmemiş",
    guest: "Misafir",
    markRead: "Okundu İşaretle",
    close: "Kapat",
    cancelTitle: "Sipariş iptal edilsin mi?",
    cancelText: "Bu işlem geri alınamaz. Sipariş iptal olarak işaretlenecek.",
    giveUp: "Vazgeç",
    cancelOrder: "İptal Et",
    statusUpdated: "Sipariş #{id} durumu güncellendi.",
    statusAndCash: "Sipariş #{id} tamamlandı ve kasaya aktarıldı.",
    cashAlreadyTransferred: "Bu sipariş daha önce kasaya aktarılmış.",
    cashTransferFail: "Durum güncellendi fakat kasa aktarımı yapılamadı.",
    cashZeroSkipped: "Tutar sıfır olduğu için kasaya aktarılmadı.",
    newOrderMsg: "{count} yeni sipariş geldi!",
    newWaiterMsg: "{count} yeni garson çağrısı!",
    retry: "Yeniden dene",
    openCashPanel: "Kasada Aç",
    stockNameRequired: "Ürün adı boş olamaz.",
    stockSaved: "{name} stok bilgisi kaydedildi.",
    stockDeleted: "Stok kaydı silindi.",
    stockSaveFail: "Stok kaydedilemedi",
    stockDeleteFail: "Stok silinemedi",
    stockEmptyLong: "Henüz stok girilmemiş. Stok Ekle butonuna basarak Ayran, Kola, Su gibi ürünlerin adetlerini girebilirsiniz.",
    dailyTrend: "Günlük",
    monthlyTrend: "Aylık",
    revenueTrendTitle: "{period} Ciro Trendi",
    revenueWithCurrency: "Ciro",

  },
  en: {
    pageTitle: "Order Management",
    pageSubtitle: "Manage QR menu orders, waiter calls, stock and revenue tracking from one screen.",
    soundOn: "Sound alert on",
    soundOff: "Sound alert off",
    waiterCalls: "Waiter Calls",
    refreshOrders: "Refresh orders",
    searchPlaceholder: "Search order ID, customer name or phone",
    all: "All",
    new: "New",
    preparing: "Preparing",
    completed: "Completed",
    cancelled: "Cancelled",
    table: "Table",
    customer: "Customer",
    phone: "Phone",
    note: "Note",
    total: "Total",
    markPreparing: "Preparing",
    markCompleted: "Completed",
    cancel: "Cancel",
    criticalStock: "Critical Stock",
    criticalStockSub: "Products at critical level",
    dailyRevenue: "Daily Revenue",
    totalOrders: "Total Orders",
    period: "Period",
    daily: "Daily",
    monthly: "Monthly",
    topProducts: "Best Selling Products",
    noSalesData: "No sales data yet",
    piece: "pcs",
    stockManagement: "Stock Management",
    stockHelp: "Enter product stock here. Example: Ayran 100 pcs, minimum 10 pcs.",
    addStock: "Add Stock",
    emptyStock: "No stock has been entered yet. Use Add Stock to enter product quantities.",
    productName: "Product name",
    stockQty: "Stock Quantity",
    minWarning: "Minimum Alert",
    critical: "Critical / Out",
    stockNormal: "Stock Normal",
    save: "Save",
    delete: "Delete",
    revenueTrend: "Revenue Trend",
    periodCol: "Period",
    orderCol: "Order",
    revenueCol: "Revenue",
    noFiltered: "No orders match your filters.",
    noOrders: "No orders yet.",
    noWaiter: "No waiter calls yet",
    tableUnspecified: "Not specified",
    guest: "Guest",
    markRead: "Mark as Read",
    close: "Close",
    cancelTitle: "Cancel this order?",
    cancelText: "This action cannot be undone. The order will be marked as cancelled.",
    giveUp: "Back",
    cancelOrder: "Cancel Order",
    statusUpdated: "Order #{id} status updated.",
    statusAndCash: "Order #{id} completed and transferred to cash register.",
    cashAlreadyTransferred: "This order was already transferred to cash register.",
    cashTransferFail: "Status updated but cash transfer failed.",
    cashZeroSkipped: "Amount is zero, so it was not transferred to cash register.",
    newOrderMsg: "{count} new order received!",
    newWaiterMsg: "{count} new waiter call!",
    retry: "Try again",
    openCashPanel: "Open in Cash",
    stockNameRequired: "Product name cannot be empty.",
    stockSaved: "{name} stock information saved.",
    stockDeleted: "Stock record deleted.",
    stockSaveFail: "Stock could not be saved",
    stockDeleteFail: "Stock could not be deleted",
    stockEmptyLong: "No stock has been entered yet. Use Add Stock to enter quantities for products such as Ayran, Cola or Water.",
    dailyTrend: "Daily",
    monthlyTrend: "Monthly",
    revenueTrendTitle: "{period} Revenue Trend",
    revenueWithCurrency: "Revenue",
  },
  de: {
    pageTitle: "Bestellverwaltung",
    pageSubtitle: "QR-Menü-Bestellungen, Kellnerrufe, Lager und Umsatz auf einem Bildschirm verwalten.",
    soundOn: "Tonbenachrichtigung aktiv",
    soundOff: "Tonbenachrichtigung aus",
    waiterCalls: "Kellnerrufe",
    refreshOrders: "Bestellungen aktualisieren",
    searchPlaceholder: "Bestell-ID, Kundenname oder Telefon suchen",
    all: "Alle",
    new: "Neu",
    preparing: "In Vorbereitung",
    completed: "Abgeschlossen",
    cancelled: "Storniert",
    table: "Tisch",
    customer: "Kunde",
    phone: "Telefon",
    note: "Notiz",
    total: "Gesamt",
    markPreparing: "In Vorbereitung",
    markCompleted: "Abgeschlossen",
    cancel: "Stornieren",
    criticalStock: "Kritischer Lagerbestand",
    criticalStockSub: "Produkte auf kritischem Niveau",
    dailyRevenue: "Tagesumsatz",
    totalOrders: "Bestellungen",
    period: "Zeitraum",
    daily: "Täglich",
    monthly: "Monatlich",
    topProducts: "Meistverkaufte Produkte",
    noSalesData: "Noch keine Verkaufsdaten",
    piece: "Stk.",
    stockManagement: "Lagerverwaltung",
    stockHelp: "Produktbestand hier eingeben. Beispiel: Ayran 100 Stk., Minimum 10 Stk.",
    addStock: "Bestand hinzufügen",
    emptyStock: "Noch kein Bestand eingetragen. Mit Bestand hinzufügen können Produkte erfasst werden.",
    productName: "Produktname",
    stockQty: "Bestand",
    minWarning: "Mindestwarnung",
    critical: "Kritisch / Leer",
    stockNormal: "Bestand normal",
    save: "Speichern",
    delete: "Löschen",
    revenueTrend: "Umsatztrend",
    periodCol: "Zeitraum",
    orderCol: "Bestellung",
    revenueCol: "Umsatz",
    noFiltered: "Keine Bestellungen zu diesen Filtern gefunden.",
    noOrders: "Noch keine Bestellungen.",
    noWaiter: "Noch keine Kellnerrufe",
    tableUnspecified: "Nicht angegeben",
    guest: "Gast",
    markRead: "Als gelesen markieren",
    close: "Schließen",
    cancelTitle: "Bestellung stornieren?",
    cancelText: "Diese Aktion kann nicht rückgängig gemacht werden. Die Bestellung wird storniert.",
    giveUp: "Zurück",
    cancelOrder: "Stornieren",
    statusUpdated: "Bestellung #{id} wurde aktualisiert.",
    statusAndCash: "Bestellung #{id} abgeschlossen und in die Kasse übertragen.",
    cashAlreadyTransferred: "Diese Bestellung wurde bereits in die Kasse übertragen.",
    cashTransferFail: "Status aktualisiert, aber Kassenübertragung fehlgeschlagen.",
    cashZeroSkipped: "Betrag ist null, daher keine Kassenübertragung.",
    newOrderMsg: "{count} neue Bestellung erhalten!",
    newWaiterMsg: "{count} neuer Kellnerruf!",
    retry: "Erneut versuchen",
    openCashPanel: "In Kasse öffnen",
    stockNameRequired: "Produktname darf nicht leer sein.",
    stockSaved: "{name} Lagerbestand wurde gespeichert.",
    stockDeleted: "Lagerbestand wurde gelöscht.",
    stockSaveFail: "Lagerbestand konnte nicht gespeichert werden",
    stockDeleteFail: "Lagerbestand konnte nicht gelöscht werden",
    stockEmptyLong: "Noch kein Bestand eingetragen. Mit Bestand hinzufügen können Mengen für Produkte wie Ayran, Cola oder Wasser erfasst werden.",
    dailyTrend: "Täglich",
    monthlyTrend: "Monatlich",
    revenueTrendTitle: "{period} Umsatztrend",
    revenueWithCurrency: "Umsatz",

  },
};

function getPanelLang() {
  const raw =
    localStorage.getItem("panel_dili") ||
    localStorage.getItem("app_lang") ||
    localStorage.getItem("lang") ||
    "tr";

  const lang = String(raw).slice(0, 2).toLowerCase();
  return ["tr", "en", "de"].includes(lang) ? lang : "tr";
}

function getLocale(lang) {
  if (lang === "en") return "en-US";
  if (lang === "de") return "de-DE";
  return "tr-TR";
}

function normalizeCurrency(value) {
  const c = String(value || "TRY").toUpperCase();
  return c === "TL" ? "TRY" : c;
}

function moneyText(value, currency = "TRY", lang = "tr") {
  const n = Number(value || 0);
  return `${n.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${normalizeCurrency(currency)}`;
}

function normalizePaymentMethod(value) {
  const v = String(value || "").toLowerCase();

  if (v.includes("card") || v.includes("kart") || v.includes("online")) return "card";
  if (v.includes("bank") || v.includes("banka") || v.includes("havale") || v.includes("eft")) return "bank";

  return "cash";
}

function getOrderCurrency(order) {
  return normalizeCurrency(
    order?.currency ||
      order?.para_birimi ||
      order?.items?.find((x) => x.currency)?.currency ||
      "TRY"
  );
}

function getOrderTotal(order) {
  return Number(order?.toplam_tutar || order?.total_amount || order?.total || 0);
}
// ========== ANIMATIONS ==========
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const alertPop = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.72);
  }
  55% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.08);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

const alertRing = keyframes`
  0% {
    transform: scale(0.72);
    opacity: 0.75;
  }
  70% {
    transform: scale(1.55);
    opacity: 0;
  }
  100% {
    transform: scale(1.55);
    opacity: 0;
  }
`;

const alertIconShake = keyframes`
  0%, 100% {
    transform: rotate(0deg) scale(1);
  }
  20% {
    transform: rotate(-10deg) scale(1.05);
  }
  40% {
    transform: rotate(10deg) scale(1.08);
  }
  60% {
    transform: rotate(-7deg) scale(1.05);
  }
  80% {
    transform: rotate(7deg) scale(1.04);
  }
`;

const buttonBreathe = keyframes`
  0%, 100% {
    transform: translateY(0);
    box-shadow: 0 10px 24px rgba(15,23,42,0.10);
  }
  50% {
    transform: translateY(-2px);
    box-shadow: 0 16px 34px rgba(15,23,42,0.16);
  }
`;


// ========== CUSTOM HOOK: useOrders (unchanged) ==========
const useOrders = (token, pollIntervalMs = 15000) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastNewOrderId, setLastNewOrderId] = useState(null);
  const isFirstLoad = useRef(true);

  const fetchOrders = useCallback(async () => {
    if (!token) throw new Error("Token eksik");
    const res = await fetch(`${API_BASE}/qr-orders`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return json.data || [];
  }, [token]);

  const loadOrders = useCallback(async () => {
    if (!token) return;
    setError(null);
    try {
      const newOrders = await fetchOrders();
      setOrders(newOrders);
      if (newOrders.length > 0) {
        const currentMaxId = Math.max(...newOrders.map((o) => o.id));
        if (!isFirstLoad.current && lastNewOrderId !== null && currentMaxId > lastNewOrderId) {
          // yeni sipariş
        }
        setLastNewOrderId(currentMaxId);
      }
      isFirstLoad.current = false;
    } catch (err) {
      setError(err.message || "Siparişler alınamadı");
    } finally {
      setLoading(false);
    }
  }, [token, fetchOrders, lastNewOrderId]);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, pollIntervalMs);
    return () => clearInterval(interval);
  }, [loadOrders, pollIntervalMs]);

  const updateOrderStatus = useCallback(async (orderId, newStatus) => {
    if (!token) throw new Error("Token eksik");
    const previousOrders = [...orders];
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId ? { ...order, durum: newStatus } : order
      )
    );
    try {
      const res = await fetch(`${API_BASE}/qr-orders/${orderId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ durum: newStatus }),
      });
      if (!res.ok) throw new Error("Durum güncellenemedi");
      await loadOrders();
    } catch (err) {
      setOrders(previousOrders);
      throw new Error(err.message);
    }
  }, [token, orders, loadOrders]);

  return { orders, loading, error, updateOrderStatus, refetch: loadOrders };
};

// ========== ERROR BOUNDARY (unchanged) ==========
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const GlobalErrorFallback = () => (
  <Box sx={{ p: 4, textAlign: "center" }}>
    <Alert severity="error">
      Bir hata oluştu. Lütfen sayfayı yenileyin.
      <Button sx={{ ml: 2 }} variant="outlined" onClick={() => window.location.reload()}>
        Yenile
      </Button>
    </Alert>
  </Box>
);







// ========== OrderCard - PROFESSIONAL MOBILE RESTYLED ==========
const OrderCard = ({
  order,
  onStatusChange,
  onCancelClick,
  onDeleteClick,
  statusColor,
  statusLabel,
  formatDate,
  TT,
  lang,
}) => {
  const [updating, setUpdating] = useState(false);
  const theme = useTheme();

  const handleAction = async (newStatus) => {
    if (updating) return;

    setUpdating(true);
    try {
      await onStatusChange(order.id, newStatus);
    } finally {
      setUpdating(false);
    }
  };

  const borderColor = {
    yeni: theme.palette.warning.main,
    hazirlaniyor: theme.palette.info.main,
    tamamlandi: theme.palette.success.main,
    iptal: theme.palette.error.main,
  }[order.durum] || theme.palette.grey[400];

  const StatusIcon = () => {
    switch (order.durum) {
      case "yeni":
        return <NewReleasesIcon fontSize="small" sx={{ mr: 0.5 }} />;
      case "hazirlaniyor":
        return <LocalDiningIcon fontSize="small" sx={{ mr: 0.5 }} />;
      case "tamamlandi":
        return <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />;
      case "iptal":
        return <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />;
      default:
        return null;
    }
  };

  const currency = getOrderCurrency(order);
  const total = getOrderTotal(order);

  const infoChipSx = {
    borderRadius: 999,
    fontWeight: 700,
    bgcolor: alpha(theme.palette.primary.main, 0.06),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    maxWidth: "100%",
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };

  return (
    <Card
    sx={{
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  borderRadius: { xs: 3, sm: 4 },
  overflow: "hidden",
        border: `1px solid ${alpha(borderColor, 0.24)}`,
        borderLeft: `7px solid ${borderColor}`,
        background: `linear-gradient(180deg, ${alpha("#ffffff", 0.96)} 0%, ${alpha(
          theme.palette.background.paper,
          0.92
        )} 100%)`,
        boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
        transition: "all 0.22s ease",
        animation: `${fadeInUp} 0.28s ease-out`,
        "&:hover": {
          transform: { xs: "none", sm: "translateY(-3px)" },
          boxShadow: "0 24px 55px rgba(15,23,42,0.12)",
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.8, sm: 2.5, md: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.4}
          sx={{ mb: 1.8 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                letterSpacing: -0.4,
                color: "text.primary",
                lineHeight: 1.15,
              }}
            >
              #{order.id}
            </Typography>

            {order.created_at && (
              <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.6 }}>
                <AccessTimeIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(order.created_at)}
                </Typography>
              </Stack>
            )}
          </Box>

          <Chip
            icon={<StatusIcon />}
            label={statusLabel(order.durum)}
            color={statusColor(order.durum)}
            size="small"
            sx={{
              fontWeight: 800,
              px: 0.7,
              borderRadius: 999,
              alignSelf: { xs: "flex-start", sm: "center" },
              ...(order.durum === "yeni" && {
                animation: `${pulse} 1.2s infinite`,
              }),
            }}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mb: 1.8 }}
        >
          <Chip size="small" sx={infoChipSx} label={`${TT.table}: ${order.masa_no || "-"}`} />
          <Chip size="small" sx={infoChipSx} label={`${TT.customer}: ${order.musteri_adi || "-"}`} />
          <Chip size="small" sx={infoChipSx} label={`${TT.phone}: ${order.telefon || "-"}`} />
        </Stack>

      


<Divider sx={{ my: 1.5, borderStyle: "dashed" }} />

<Stack spacing={0.9}>
  {(order.items || []).map((item, idx) => {
    const qty = item.quantity || item.adet || 1;
    const title = item.title || item.urun_adi || item.name || "-";
    const price = item.price || item.unit_price || item.fiyat || 0;

    return (
      <Box
        key={idx}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) auto" },
          alignItems: "center",
          gap: { xs: 0.8, md: 1.2 },
          py: { xs: 1.1, md: 1 },
          px: { xs: 1, sm: 1.2 },
          borderRadius: 2.7,
          bgcolor: alpha(theme.palette.grey[500], 0.04),
          border: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, width: "100%" }}
        >
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.dark,
              px: 1,
              py: 0.35,
              borderRadius: 999,
              fontSize: "0.74rem",
              fontWeight: 900,
            }}
          >
            {qty}x
          </Box>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 850,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.primary",
              fontSize: { xs: "0.9rem", md: "0.95rem" },
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 950,
            whiteSpace: "nowrap",
            color: "primary.main",
            textAlign: { xs: "left", md: "right" },
            pl: { xs: 4.8, md: 0 },
            fontSize: { xs: "0.95rem", md: "0.9rem" },
          }}
        >
          {moneyText(price, item.currency || currency, lang)}
        </Typography>
      </Box>
    );
  })}
</Stack>

{order.siparis_notu && (
  <Alert
    severity="warning"
    sx={{
      mt: 2,
      fontSize: "0.84rem",
      borderRadius: 2.5,
      alignItems: "center",
    }}
  >
    <b>{TT.note}:</b> {order.siparis_notu}
  </Alert>
)}

<Box
  sx={{
    mt: 2,
    p: 1.5,
    borderRadius: 3,
    bgcolor: alpha(theme.palette.success.main, 0.08),
    border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
    textAlign: "right",
  }}
>
  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
    {TT.total}
  </Typography>

  <Typography
    variant="h5"
    sx={{
      fontWeight: 950,
      color: theme.palette.success.dark,
      letterSpacing: -0.5,
      lineHeight: 1.15,
    }}
  >
    {moneyText(total, currency, lang)}
  </Typography>
</Box>

<Stack
  direction={{ xs: "column", md: "row" }}
  spacing={1.25}
  sx={{
    mt: 2.4,
    width: "100%",
    minWidth: 0,
    "& .MuiButton-root": {
      minHeight: { xs: 58, md: 58 },
      borderRadius: 3,
      textTransform: "none",
      fontWeight: 950,
      whiteSpace: "nowrap",
      fontSize: { xs: "0.98rem", md: "1rem" },
      letterSpacing: -0.2,
      transition: "all 0.22s ease",
      animation: `${buttonBreathe} 2.4s ease-in-out infinite`,
      boxShadow: "0 12px 28px rgba(15,23,42,0.12)",
      "&:hover": {
        transform: "translateY(-2px) scale(1.015)",
        boxShadow: "0 18px 38px rgba(15,23,42,0.18)",
      },
      "&.Mui-disabled": {
        animation: "none",
        boxShadow: "none",
        opacity: 0.52,
      },
    },
  }}
>
  <Button
    fullWidth
    variant="contained"
    disabled={
      updating ||
      order.durum === "hazirlaniyor" ||
      order.durum === "tamamlandi" ||
      order.durum === "iptal"
    }
    onClick={() => handleAction("hazirlaniyor")}
    sx={{
      py: 1.15,
      borderRadius: 2.5,
      textTransform: "none",
      fontWeight: 900,
    }}
  >
    🍽️ {TT.markPreparing}
  </Button>

  <Button
    fullWidth
    color="success"
    variant="contained"
    disabled={updating || order.durum === "tamamlandi" || order.durum === "iptal"}
    onClick={() => handleAction("tamamlandi")}
    sx={{
      py: 1.15,
      borderRadius: 2.5,
      textTransform: "none",
      fontWeight: 900,
    }}
  >
    ✅ {TT.markCompleted}
  </Button>

  <Button
    fullWidth
    color="error"
    variant="outlined"
    disabled={updating || order.durum === "iptal"}
    onClick={() => onCancelClick(order.id)}
    sx={{
      py: 1.15,
      borderRadius: 2.5,
      textTransform: "none",
      fontWeight: 900,
      borderWidth: 2,
      "&:hover": { borderWidth: 2 },
    }}
  >
    ❌ {TT.cancel}
  </Button>

<Button
  fullWidth
  color="error"
  variant="contained"
  disabled={updating}
  onClick={() => onDeleteClick(order.id)}
  sx={{
    py: 1.15,
    borderRadius: 2.5,
    textTransform: "none",
    fontWeight: 950,
    bgcolor: "#991b1b",
    "&:hover": {
      bgcolor: "#7f1d1d",
    },
  }}
>
  🗑️ {TT.delete}
</Button>

  {order.durum === "tamamlandi" && (
    <Button
      fullWidth
      variant="outlined"
      color="primary"
      onClick={() => {
        window.location.hash = "/kasa-tahsilat";
      }}
      sx={{
        py: 1.15,
        borderRadius: 2.5,
        textTransform: "none",
        fontWeight: 900,
        borderWidth: 2,
        "&:hover": { borderWidth: 2 },
      }}
    >
      💰 {TT.openCashPanel}
    </Button>
  )}
</Stack>



      </CardContent>
    </Card>
  );
};




// ========== ANA BİLEŞEN - MODERN CORPORATE MOBILE STYLE ==========
export default function SiparislerPage() {
  const token = localStorage.getItem("token");
  const theme = useTheme();
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem("soundEnabled");
    return saved === "true";
  });
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
const [centerAlert, setCenterAlert] = useState({
  open: false,
  type: "order",
  icon: "🔔",
  title: "",
  message: "",
  subtitle: "",
});

const centerAlertTimerRef = useRef(null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  // ✅ RAPORLAMA STATELERİ
  const [statsPeriod, setStatsPeriod] = useState("day");
  const [statsData, setStatsData] = useState([]);
  const [productSales, setProductSales] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
// ✅ STOK YÖNETİMİ
const [stocks, setStocks] = useState([]);
const [stockLoading, setStockLoading] = useState(false);


  // ✅ GARSON ÇAĞRI STATELERİ
  const [waiterCalls, setWaiterCalls] = useState([]);
  const [unreadWaiterCount, setUnreadWaiterCount] = useState(0);
  const [showWaiterDialog, setShowWaiterDialog] = useState(false);
  const previousWaiterCallsRef = useRef([]);

  const previousOrdersRef = useRef([]);

  const audioRef = useRef(null);
  const waiterAudioRef = useRef(null); // ✅ SADECE BURADA TANIMLI

  const { orders, loading, error, updateOrderStatus, refetch } = useOrders(
    token,
    ORDER_POLL_INTERVAL_MS
  );



  const [lang, setLang] = useState(getPanelLang);
  const TT = ORDER_TEXT[lang] || ORDER_TEXT.tr;

const showCenterAlert = useCallback((payload) => {
  if (centerAlertTimerRef.current) {
    clearTimeout(centerAlertTimerRef.current);
  }

  setCenterAlert({
    open: true,
    type: payload.type || "order",
    icon: payload.icon || "🔔",
    title: payload.title || "",
    message: payload.message || "",
    subtitle: payload.subtitle || "",
  });

  centerAlertTimerRef.current = setTimeout(() => {
 setCenterAlert((prev) => ({
  ...prev,
  open: false,
}))
  }, 4200);
}, []);

const playPanelSound = useCallback(
  (audioEl) => {
    if (!soundEnabled || !audioEl) return;

    try {
      audioEl.currentTime = 0;
      const playPromise = audioEl.play();

      if (playPromise?.catch) {
        playPromise.catch((err) =>
          console.warn("Ses çalınamadı:", err)
        );
      }
    } catch (err) {
      console.warn("Ses çalınamadı:", err);
    }
  },
  [soundEnabled]
);

useEffect(() => {
  return () => {
    if (centerAlertTimerRef.current) {
      clearTimeout(centerAlertTimerRef.current);
    }
  };
}, []);


  useEffect(() => {
    const syncLang = () => setLang(getPanelLang());

    window.addEventListener("app_lang_change", syncLang);
    window.addEventListener("storage", syncLang);

    return () => {
      window.removeEventListener("app_lang_change", syncLang);
      window.removeEventListener("storage", syncLang);
    };
  }, []);


 // ✅ STOKLARI ÇEK
const fetchStocks = useCallback(async () => {
  setStockLoading(true);

  try {
    const currentToken = localStorage.getItem("token");

    const res = await fetch(`${API_BASE}/menu/stock`, 
{
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    const json = await res.json();

    if (json.ok) {
      setStocks(json.data || []);
    }
  } catch (err) {
    console.log("Stok alınamadı", err);
  } finally {
    setStockLoading(false);
  }
}, []);

const saveStock = async (item) => {
  const currentToken = localStorage.getItem("token");

  const urunAdi = String(item.title || item.urun_adi || "").trim();

  if (!urunAdi) {
    setSnackbar({
      open: true,
message: TT.stockNameRequired,
      severity: "error",
    });
    return;
  }

  const isNew = String(item.id).startsWith("new-");

  const res = await fetch(
    isNew ? `${API_BASE}/menu/stock` : `${API_BASE}/menu/stock/${item.id}`,
    {
      method: isNew ? "POST" : "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify({
        urun_adi: urunAdi,
        stok_adedi: Number(item.stok_adedi || 0),
        minimum_stok: Number(item.minimum_stok || 0),
        stok_takibi: true,
      }),
    }
  );

  const json = await res.json();

  if (!res.ok || !json.ok) {
throw new Error(json.message || TT.stockSaveFail);
  }

  await fetchStocks();

  setSnackbar({
    open: true,
    message: TT.stockSaved.replace("{name}", urunAdi),
    severity: "success",
  });
};

const deleteStock = async (item) => {
  const isNew = String(item.id).startsWith("new-");

  if (isNew) {
    setStocks((prev) => prev.filter((s) => s.id !== item.id));
    return;
  }

  const currentToken = localStorage.getItem("token");

  const res = await fetch(`${API_BASE}/menu/stock/${item.id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${currentToken}`,
    },
  });

  const json = await res.json();

  if (!res.ok || !json.ok) {
throw new Error(json.message || TT.stockDeleteFail);
  }

  await fetchStocks();

  setSnackbar({
    open: true,
   message: TT.stockDeleted,
    severity: "success",
  });
};


// ✅ RAPORLAMA FONKSİYONU
const fetchStatistics = useCallback(async () => {

  setStatsLoading(true);

  try {

    const currentToken = localStorage.getItem("token");

    const statsRes = await fetch(
      `${API_BASE}/menu/orders-statistics?groupBy=${statsPeriod}`,
      {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    const statsJson = await statsRes.json();

    if (statsJson.ok) {
      setStatsData(statsJson.data || []);
    }


    const productRes = await fetch(
      `${API_BASE}/menu/product-sales?limit=10`,
      {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          "Content-Type": "application/json",
        },
      }
    );


    const productJson = await productRes.json();

    if (productJson.ok) {
      setProductSales(productJson.data || []);
    }


  } catch (err) {

    console.error("Raporlama hatası:", err);

  } finally {

    setStatsLoading(false);

  }

}, [statsPeriod]);


  useEffect(() => {
    if (token) fetchStatistics();
  }, [token, statsPeriod, fetchStatistics]);

useEffect(()=>{

 if(token){
   fetchStocks();
 }

},[token, fetchStocks]);

  // ✅ GARSON ÇAĞRILARINI ÇEK (fetchStatistics'ten sonra ekle)
  const fetchWaiterCalls = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/waiter-calls`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      const json = await res.json();
      if (json.ok) {
        setWaiterCalls(json.data);
        setUnreadWaiterCount(json.data.filter(call => !call.is_read).length);
        
        // Yeni çağrı varsa ses çal
        if (previousWaiterCallsRef.current.length > 0) {
          const oldIds = new Set(previousWaiterCallsRef.current.map(c => c.id));
          const newCalls = json.data.filter(c => !oldIds.has(c.id) && !c.is_read);


     if (newCalls.length > 0) {
  playPanelSound(waiterAudioRef.current);

  showCenterAlert({
    type: "waiter",
    icon: "🔔",
    title: TT.waiterCalls,
    message: TT.newWaiterMsg.replace("{count}", newCalls.length),
    subtitle: newCalls[0]?.masa_no
      ? `${TT.table} #${newCalls[0].masa_no}`
      : TT.tableUnspecified,
  });

  setSnackbar({
    open: true,
    message: `🔔 ${TT.newWaiterMsg.replace("{count}", newCalls.length)}`,
    severity: "info",
  });
}


        }
        previousWaiterCallsRef.current = json.data;
      }
    } catch (err) {
      console.error("Garson çağrıları alınamadı:", err);
    }
}, [soundEnabled, TT, playPanelSound, showCenterAlert]);


  // ✅ GARSON ÇAĞRISINI OKUNDU İŞARETLE
  const markWaiterCallAsRead = async (callId) => {
    try {
      const currentToken = localStorage.getItem("token");
      await fetch(`${API_BASE}/waiter-calls/${callId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      fetchWaiterCalls(); // Yenile
    } catch (err) {
      console.error("Okundu işaretlenemedi:", err);
    }
  };


 // Garson çağrılarını hızlı kontrol et
  useEffect(() => {
    if (!token) return;

    fetchWaiterCalls();

    const interval = setInterval(
      fetchWaiterCalls,
      WAITER_POLL_INTERVAL_MS
    );

    return () => clearInterval(interval);
  }, [token, fetchWaiterCalls]);


// ✅ Panel sekmesi tekrar açılınca veya ekrana dönülünce beklemeden kontrol et
useEffect(() => {
  if (!token) return;

  const instantRefresh = () => {
    if (document.visibilityState === "visible") {
      refetch();
      fetchWaiterCalls();
    }
  };

  window.addEventListener("focus", instantRefresh);
  document.addEventListener("visibilitychange", instantRefresh);

  return () => {
    window.removeEventListener("focus", instantRefresh);
    document.removeEventListener("visibilitychange", instantRefresh);
  };
}, [token, refetch, fetchWaiterCalls]);



// ✅ Yeni sipariş gelince görsel uyarı her zaman gelir.
// Ses sadece "Sesli uyarı açık" ise çalar.
useEffect(() => {
  if (previousOrdersRef.current.length > 0) {
    const oldIds = new Set(previousOrdersRef.current.map((o) => o.id));
    const newOrders = orders.filter((o) => !oldIds.has(o.id));

    if (newOrders.length > 0) {
      playPanelSound(audioRef.current);

      showCenterAlert({
        type: "order",
        icon: "🛒",
        title: TT.pageTitle,
        message: TT.newOrderMsg.replace("{count}", newOrders.length),
        subtitle: newOrders[0]?.masa_no
          ? `${TT.table} #${newOrders[0].masa_no}`
          : `#${newOrders[0]?.id || ""}`,
      });

      setSnackbar({
        open: true,
        message: TT.newOrderMsg.replace("{count}", newOrders.length),
        severity: "info",
      });
    }
  }

  previousOrdersRef.current = orders;
}, [orders, TT, playPanelSound, showCenterAlert]);

  useEffect(() => {
    localStorage.setItem("soundEnabled", String(soundEnabled));
  }, [soundEnabled]);

  const transferOrderToCash = useCallback(
    async (order) => {
      if (!order) return { skipped: true, message: "" };

      const amount = getOrderTotal(order);
      const currency = getOrderCurrency(order);

      if (amount <= 0) {
        return {
          skipped: true,
          message: TT.cashZeroSkipped,
        };
      }

      const currentToken = localStorage.getItem("token");
      const marker = `QR_ORDER_ID:${order.id}`;

      const listRes = await fetch(`${API_BASE}/cash`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      if (listRes.ok) {
        const listJson = await listRes.json().catch(() => ({}));
        const alreadyExists = (listJson.data || []).some(
          (r) =>
            r.module_type === "menu" &&
            String(r.note || "").includes(marker)
        );

        if (alreadyExists) {
          return {
            skipped: true,
            message: TT.cashAlreadyTransferred,
          };
        }
      }

      const itemSummary = (order.items || [])
        .map((item) => {
          const qty = item.quantity || item.adet || 1;
          const title = item.title || item.urun_adi || item.name || "-";
          return `${qty}x ${title}`;
        })
        .join(", ");

      const body = {
        module_type: "menu",
        transaction_type: "income",
        title: `QR Menü Sipariş #${order.id}`,
        category: "QR Menü Sipariş",
        quantity: 1,
        unit_price: amount,
        total_amount: amount,
        currency,
        payment_method: normalizePaymentMethod(
          order.payment_method ||
            order.odeme_tipi ||
            order.odeme_turu ||
            order.paymentType ||
            "cash"
        ),
        customer_id: null,
        customer_name: order.musteri_adi || "",
        staff_name: "",
        note: [
          marker,
          order.masa_no ? `Masa: ${order.masa_no}` : "",
          order.telefon ? `Telefon: ${order.telefon}` : "",
          itemSummary ? `Ürünler: ${itemSummary}` : "",
          order.siparis_notu ? `Sipariş Notu: ${order.siparis_notu}` : "",
        ]
          .filter(Boolean)
          .join(" | "),
        transaction_date: String(order.created_at || new Date().toISOString()).slice(0, 10),
      };

      const res = await fetch(`${API_BASE}/cash`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
        body: JSON.stringify(body),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.ok === false) {
        throw new Error(json.message || TT.cashTransferFail);
      }

      return {
        skipped: false,
        message: TT.statusAndCash.replace("{id}", order.id),
      };
    },
    [TT]
  );



  const handleStatusUpdate = async (id, newStatus) => {
    const selectedOrder = orders.find((o) => String(o.id) === String(id));

    try {
      await updateOrderStatus(id, newStatus);

      if (newStatus === "tamamlandi") {
        try {
          const cashResult = await transferOrderToCash(selectedOrder);

          setSnackbar({
            open: true,
            message:
              cashResult?.message ||
              TT.statusAndCash.replace("{id}", id),
            severity: cashResult?.skipped ? "info" : "success",
          });

          return;
        } catch (cashErr) {
          setSnackbar({
            open: true,
            message: cashErr.message || TT.cashTransferFail,
            severity: "warning",
          });

          return;
        }
      }

      setSnackbar({
        open: true,
        message: TT.statusUpdated.replace("{id}", id),
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Güncelleme başarısız",
        severity: "error",
      });
    }
  };

  const handleCancelClick = (id) => {
    setSelectedOrderId(id);
    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (selectedOrderId) {
      await handleStatusUpdate(selectedOrderId, "iptal");
      setCancelDialogOpen(false);
      setSelectedOrderId(null);
    }
  };

const deleteOrderPermanently = async (id) => {
  if (!window.confirm(`#${id} siparişi tamamen silinsin mi?`)) return;

  try {
    const currentToken = localStorage.getItem("token");

    const res = await fetch(`${API_BASE}/qr-orders/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    const json = await res.json().catch(() => ({}));

    if (!res.ok || json.ok === false) {
      throw new Error(json.message || "Sipariş silinemedi");
    }

    await refetch();

    setSnackbar({
      open: true,
      message: `Sipariş #${id} tamamen silindi.`,
      severity: "success",
    });
  } catch (err) {
    setSnackbar({
      open: true,
      message: err.message || "Sipariş silinemedi",
      severity: "error",
    });
  }
};


  const filteredOrders = useMemo(() => {
    let result = [...orders];
    if (filterStatus !== "all") {
      result = result.filter((order) => order.durum === filterStatus);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (order) =>
          order.id.toString().includes(term) ||
          (order.musteri_adi && order.musteri_adi.toLowerCase().includes(term)) ||
          (order.telefon && order.telefon.includes(term))
      );
    }
    result.sort((a, b) => b.id - a.id);
    return result;
  }, [orders, filterStatus, searchTerm]);

  const itemsPerPage = 10;
  const pageCount = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  useEffect(() => {
    setPage(1);
  }, [filterStatus, searchTerm]);

  const statusColor = (durum) => {
    switch (durum) {
      case "yeni":
        return "warning";
      case "hazirlaniyor":
        return "info";
      case "tamamlandi":
        return "success";
      case "iptal":
        return "error";
      default:
        return "default";
    }
  };




   const statusLabel = (durum) => {
    switch (durum) {
      case "yeni":
        return TT.new;
      case "hazirlaniyor":
        return TT.preparing;
      case "tamamlandi":
        return TT.completed;
      case "iptal":
        return TT.cancelled;
      default:
        return durum;
    }
  };


const filterButtonSx = (value, color) => ({
  borderRadius: "14px !important",
  px: { xs: 1, md: 2 },
  py: { xs: 1.1, md: 1 },
  minHeight: { xs: 44, md: 42 },
  textTransform: "none",
  fontWeight: 950,
  fontSize: { xs: "0.78rem", md: "0.86rem" },
  whiteSpace: { xs: "normal", md: "nowrap" },
  lineHeight: 1.15,
  color: filterStatus === value ? "#fff !important" : `${color} !important`,
  bgcolor:
    filterStatus === value
      ? `${color} !important`
      : `${alpha(color, 0.08)} !important`,
  border: `1px solid ${alpha(color, 0.28)} !important`,
  boxShadow:
    filterStatus === value
      ? `0 10px 24px ${alpha(color, 0.24)}`
      : "none",
  "&:hover": {
    bgcolor:
      filterStatus === value
        ? `${color} !important`
        : `${alpha(color, 0.14)} !important`,
  },
});

const formatDate = (isoString) => {
  if (!isoString) return "";

  try {
    const raw = String(isoString).trim();

    // Backend bazen "2026-06-25 13:10:00" gibi timezone'suz gönderebilir.
    // Bu durumda UTC kabul edip Türkiye saatine çeviriyoruz.
    const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");

    const hasTimezone =
      /Z$/i.test(normalized) || /[+-]\d{2}:\d{2}$/.test(normalized);

    const date = new Date(hasTimezone ? normalized : `${normalized}Z`);

    return new Intl.DateTimeFormat(getLocale(lang), {
      timeZone: "Europe/Istanbul",
      dateStyle: "short",
      timeStyle: "short",
    }).format(date);
  } catch {
    return String(isoString);
  }
};


  // Clear search handler
  const handleClearSearch = () => setSearchTerm("");

 
// Refresh with rotating animation
const [isRefreshing, setIsRefreshing] = useState(false);

const handleRefresh = async () => {
  setIsRefreshing(true);
  await refetch();
  setTimeout(() => setIsRefreshing(false), 500);
};

// ✅ Mobilde seçili kartları sayfaya tam yaslamak için
const mobileFullBleedSx = {
  width: { xs: "100vw", sm: "100%" },
  maxWidth: { xs: "100vw", sm: "100%" },
  ml: { xs: "calc(50% - 50vw)", sm: 0 },
  mr: { xs: "calc(50% - 50vw)", sm: 0 },
};


  // Loading skeleton with shimmer
  if (loading && orders.length === 0) {
    return (
      <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #f8fafc 0%, #eef2f5 100%)", p: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Skeleton variant="text" width={220} height={48} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={280} height={32} sx={{ mb: 3 }} />
          {[1, 2, 3].map((i) => (
            <Card key={i} sx={{ mb: 2, borderRadius: 4, overflow: "hidden" }}>
              <CardContent>
                <Skeleton variant="text" width="40%" height={32} />
                <Skeleton variant="text" width="20%" />
                <Divider sx={{ my: 1 }} />
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                <Skeleton variant="text" width="30%" sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }


return (
  <ErrorBoundary fallback={<GlobalErrorFallback />}>
    <audio ref={audioRef} src="/sounds/new-order.mp3" preload="auto" />
    <audio ref={waiterAudioRef} src="/sounds/waiter-call.mp3" preload="auto" />

    {centerAlert.open && (
  <Box
    onClick={() =>
      setCenterAlert((prev) => ({
        ...prev,
        open: false,
      }))
    }
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: 9997,
      bgcolor: "rgba(15,23,42,0.42)",
      backdropFilter: "blur(7px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: { xs: 2, md: 4 },
    }}
  >
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: { xs: 360, sm: 460, md: 560 },
        borderRadius: { xs: 4, md: 5 },
        p: { xs: 3, sm: 4, md: 5 },
        textAlign: "center",
        overflow: "hidden",
        background:
          centerAlert.type === "waiter"
            ? "linear-gradient(145deg, #fff7ed 0%, #ffffff 58%, #ffedd5 100%)"
            : "linear-gradient(145deg, #eff6ff 0%, #ffffff 58%, #dbeafe 100%)",
        border:
          centerAlert.type === "waiter"
            ? "2px solid rgba(245,130,32,0.35)"
            : "2px solid rgba(11,77,139,0.24)",
        boxShadow:
          centerAlert.type === "waiter"
            ? "0 30px 80px rgba(245,130,32,0.35)"
            : "0 30px 80px rgba(11,77,139,0.28)",
        animation: `${alertPop} 0.45s ease-out`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "auto auto 16px 16px",
          width: 86,
          height: 86,
          borderRadius: "50%",
          bgcolor:
            centerAlert.type === "waiter"
              ? "rgba(245,130,32,0.12)"
              : "rgba(11,77,139,0.12)",
          animation: `${alertRing} 1.55s ease-out infinite`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: "18px 18px auto auto",
          width: 92,
          height: 92,
          borderRadius: "50%",
          bgcolor:
            centerAlert.type === "waiter"
              ? "rgba(245,130,32,0.10)"
              : "rgba(11,77,139,0.10)",
          animation: `${alertRing} 1.8s ease-out infinite`,
        }}
      />

      <Box
        sx={{
          width: { xs: 96, md: 118 },
          height: { xs: 96, md: 118 },
          borderRadius: "50%",
          mx: "auto",
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: { xs: 48, md: 60 },
          background:
            centerAlert.type === "waiter"
              ? "linear-gradient(135deg, #f97316, #f59e0b)"
              : "linear-gradient(135deg, #0b4d8b, #2563eb)",
          color: "#fff",
          boxShadow:
            centerAlert.type === "waiter"
              ? "0 18px 42px rgba(249,115,22,0.38)"
              : "0 18px 42px rgba(37,99,235,0.34)",
          animation: `${alertIconShake} 0.95s ease-in-out infinite`,
        }}
      >
        {centerAlert.icon}
      </Box>

      <Typography
        sx={{
          fontWeight: 950,
          fontSize: { xs: 24, sm: 30, md: 38 },
          lineHeight: 1.1,
          color: "#0f172a",
          letterSpacing: -1,
        }}
      >
        {centerAlert.message}
      </Typography>

      <Typography
        sx={{
          mt: 1.2,
          fontWeight: 900,
          fontSize: { xs: 15, md: 18 },
          color:
            centerAlert.type === "waiter"
              ? "#c2410c"
              : "#0b4d8b",
        }}
      >
        {centerAlert.title}
      </Typography>

      {centerAlert.subtitle && (
        <Typography
          sx={{
            mt: 1,
            color: "text.secondary",
            fontWeight: 800,
            fontSize: { xs: 14, md: 16 },
          }}
        >
          {centerAlert.subtitle}
        </Typography>
      )}

      <Typography
        sx={{
          mt: 2.2,
          color: "text.secondary",
          fontSize: { xs: 12, md: 13 },
          fontWeight: 700,
        }}
      >
        Kapatmak için dokunabilirsiniz.
      </Typography>
    </Box>
  </Box>
)}

  

<Box
  sx={{
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100vw",
    minWidth: 0,
    overflowX: "hidden",
    background:
      "radial-gradient(circle at top left, rgba(21,101,192,0.10), transparent 32%), radial-gradient(circle at top right, rgba(245,130,32,0.12), transparent 30%), linear-gradient(145deg, #f8fafc 0%, #eef2f7 100%)",
    p: { xs: 1, sm: 1.5, md: 2.5 },
    pb: { xs: 5, md: 7 },
    boxSizing: "border-box",

    "& *": {
      boxSizing: "border-box",
    },

    "& .MuiCard-root, & .MuiPaper-root": {
      maxWidth: "100%",
      minWidth: 0,
    },

    "& .MuiButton-root, & .MuiToggleButton-root": {
      minWidth: 0,
    },

    "& .MuiStack-root, & .MuiGrid-root": {
      minWidth: 0,
    },

    "& .MuiTypography-root": {
      maxWidth: "100%",
    },

    "@media (max-width:899px)": {
      p: 1,
      overflowX: "hidden",
      "& body": {
        overflowX: "hidden",
      },
    },
  }}
>


{/* Header - Kasa tarzı kurumsal mobil uyum */}
<Card
  sx={{
    mb: { xs: 1.5, md: 3 },
    borderRadius: { xs: 3, md: 5 },
    position: { xs: "sticky", md: "relative" },
    top: { xs: 0, md: "auto" },
    zIndex: 20,
    overflow: "hidden",
    width: "100%",
    minWidth: 0,
    border: "1px solid rgba(15,23,42,0.10)",
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(248,250,252,0.94) 100%)",
    backdropFilter: "blur(14px)",
    boxShadow: {
      xs: "0 10px 28px rgba(15,23,42,0.08)",
      md: "0 24px 70px rgba(15,23,42,0.10)",
    },

    "&::before": {
      content: '""',
      display: "block",
      height: 5,
      background: "linear-gradient(90deg, #0b4d8b, #f58220)",
    },

    "&::after": {
      content: '""',
      position: "absolute",
      right: { xs: -95, md: -70 },
      top: { xs: -95, md: -70 },
      width: { xs: 170, md: 210 },
      height: { xs: 170, md: 210 },
      borderRadius: "50%",
      background: "rgba(245,130,32,0.13)",
      pointerEvents: "none",
    },
  }}
>
  <CardContent sx={{ p: { xs: 1.6, sm: 2.2, md: 3 } }}>
    <Stack
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", md: "center" }}
      spacing={2}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h5"
        sx={{
  fontWeight: 950,
  fontSize: { xs: 22, sm: 26, md: 34 },
  lineHeight: 1.08,
  wordBreak: "break-word",
}}
        >
          📦 {TT.pageTitle}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mt: 0.8,
            maxWidth: 760,
            fontSize: { xs: 12.5, sm: 14 },
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: { xs: 2, md: 3 },
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {TT.pageSubtitle}
        </Typography>
      </Box>

    <Stack
  direction={{ xs: "column", md: "row" }}
  spacing={1}
  sx={{
    width: { xs: "100%", md: "auto" },
    minWidth: 0,
    position: "relative",
    zIndex: 1,

    "& .MuiButton-root": {
      width: { xs: "100%", md: "auto" },
      minHeight: { xs: 54, md: 58 },
      borderRadius: 3,
      px: { xs: 2, md: 2.6 },
      textTransform: "none",
      fontWeight: 950,
      whiteSpace: "nowrap",
      fontSize: { xs: "0.92rem", md: "1rem" },
      minWidth: { xs: 0, md: 150 },
    },
  }}
>
        <Button
          fullWidth
          variant={soundEnabled ? "contained" : "outlined"}
          color={soundEnabled ? "success" : "inherit"}
          startIcon={soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
          onClick={() => setSoundEnabled((prev) => !prev)}
sx={{
  minHeight: { xs: 54, md: 58 },
  borderRadius: 3,
  px: { xs: 2, md: 2.6 },
  textTransform: "none",
  fontWeight: 950,
  whiteSpace: "nowrap",
  fontSize: { xs: "0.92rem", md: "1rem" },
  transition: "all 0.22s ease",
  animation: `${buttonBreathe} 2.7s ease-in-out infinite`,
  boxShadow: "0 12px 28px rgba(15,23,42,0.10)",
  "&:hover": {
    transform: "translateY(-2px) scale(1.015)",
    boxShadow: "0 18px 38px rgba(15,23,42,0.16)",
  },
  "&.Mui-disabled": {
    animation: "none",
    boxShadow: "none",
  },
}}


        >
          {soundEnabled ? TT.soundOn : TT.soundOff}
        </Button>

        <Button
          fullWidth
          variant="contained"
          color="warning"
          startIcon={<LocalDiningIcon />}
          onClick={() => setShowWaiterDialog(true)}
        
 sx={{
  position: "relative",
}}
        >
          {TT.waiterCalls}
          {unreadWaiterCount > 0 && (
            <Box
              component="span"
              sx={{
                ml: 1,
                bgcolor: "error.main",
                color: "white",
                borderRadius: 999,
                minWidth: 22,
                height: 22,
                px: 0.7,
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
              }}
            >
              {unreadWaiterCount}
            </Box>
          )}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
  sx={{}}
        >
          {TT.refreshOrders}
        </Button>
      </Stack>
    </Stack>
  </CardContent>
</Card>



          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 3 }}
              action={
                <Button color="inherit" size="small" onClick={handleRefresh}>
                {TT.retry}
                </Button>
              }
            >
              {error}
            </Alert>
          )}

         {/* Search & Filter Card */}
<Card
  sx={{
    mb: { xs: 1.5, md: 3 },
    borderRadius: { xs: 3, md: 4 },
    p: { xs: 1.4, sm: 2 },
    overflow: "hidden",
    border: "1px solid rgba(226,232,240,0.9)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.07)",
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(12px)",
  }}
>
  <Stack
    direction={{ xs: "column", lg: "row" }}
    spacing={1.4}
    alignItems="stretch"
    sx={{ width: "100%", minWidth: 0 }}
  >
    <TextField
      size="small"
      placeholder={TT.searchPlaceholder}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      sx={{
        flex: 1,
        minWidth: 0,
        "& .MuiOutlinedInput-root": {
          borderRadius: 3,
          minHeight: { xs: 46, md: 50 },
          bgcolor: "#fff",
          fontWeight: 700,
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: searchTerm && (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClearSearch}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />

    <ToggleButtonGroup
      size="small"
      value={filterStatus}
      exclusive
      onChange={(_, val) => val && setFilterStatus(val)}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2,minmax(0,1fr))",
          sm: "repeat(2,minmax(0,1fr))",
          md: "repeat(5,minmax(0,auto))",
        },
        gap: 1,
        "& .MuiToggleButtonGroup-grouped": {
          m: "0 !important",
          border: "1px solid rgba(203,213,225,0.9) !important",
        },
      }}
    >
      <ToggleButton
        value="all"
        sx={filterButtonSx("all", theme.palette.primary.main)}
      >
        {TT.all}
      </ToggleButton>

      <ToggleButton
        value="yeni"
        sx={filterButtonSx("yeni", theme.palette.warning.main)}
      >
        {TT.new}
      </ToggleButton>

      <ToggleButton
        value="hazirlaniyor"
        sx={filterButtonSx("hazirlaniyor", theme.palette.info.main)}
      >
        {TT.preparing}
      </ToggleButton>

      <ToggleButton
        value="tamamlandi"
        sx={filterButtonSx("tamamlandi", theme.palette.success.main)}
      >
        {TT.completed}
      </ToggleButton>

      <ToggleButton
        value="iptal"
        sx={filterButtonSx("iptal", theme.palette.error.main)}
      >
        {TT.cancelled}
      </ToggleButton>
    </ToggleButtonGroup>
  </Stack>
</Card>



{/* ✅ DASHBOARD KARTLARI */}
<Grid
  container
  rowSpacing={{ xs: 1.1, sm: 1.5, md: 2 }}
  columnSpacing={{ xs: 0, sm: 1.5, md: 2 }}
  sx={{
    ...mobileFullBleedSx,
    mb: { xs: 1.5, md: 3 },
    overflowX: "hidden",
  }}
>

<Grid item xs={12} sm={12} md={3}>
    <Card
      sx={{
        p: { xs: 1.35, sm: 1.7, md: 2.2 },
        borderRadius: { xs: 2.5, md: 3.5 },
        minHeight: { xs: 112, md: 132 },
        height: "100%",
        border: "1px solid rgba(226,232,240,0.9)",
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,250,252,0.92))",
        boxShadow: "0 10px 25px rgba(15,23,42,0.07)",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="subtitle2"
        color="textSecondary"
        sx={{ fontWeight: 800, fontSize: { xs: "0.76rem", md: "0.9rem" } }}
      >
        📦 {TT.criticalStock}
      </Typography>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 950,
          fontSize: { xs: 22, sm: 26, md: 34 },
          lineHeight: 1.08,
          color: "error.main",
          wordBreak: "break-word",
          mt: 0.7,
        }}
      >
        {
          stocks.filter(
            (x) => Number(x.stok_adedi) <= Number(x.minimum_stok)
          ).length
        }
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          display: "block",
          mt: 0.6,
          lineHeight: 1.25,
          fontSize: { xs: "0.68rem", md: "0.76rem" },
        }}
      >
        {TT.criticalStockSub}
      </Typography>
    </Card>
  </Grid>

<Grid item xs={12} sm={12} md={3}>
    <Card
      sx={{
        p: { xs: 1.35, sm: 1.7, md: 2.2 },
        borderRadius: { xs: 2.5, md: 3.5 },
        minHeight: { xs: 112, md: 132 },
        height: "100%",
        border: "1px solid rgba(226,232,240,0.9)",
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,250,252,0.92))",
        boxShadow: "0 10px 25px rgba(15,23,42,0.07)",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="subtitle2"
        color="textSecondary"
        sx={{ fontWeight: 800, fontSize: { xs: "0.76rem", md: "0.9rem" } }}
      >
        💰 {TT.dailyRevenue}
      </Typography>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 950,
          fontSize: { xs: 20, sm: 24, md: 32 },
          lineHeight: 1.08,
          color: theme.palette.primary.main,
          wordBreak: "break-word",
          mt: 0.7,
        }}
      >
        {statsData
          .reduce((sum, item) => sum + Number(item.ciro || 0), 0)
          .toFixed(2)}{" "}
        TL
      </Typography>
    </Card>
  </Grid>

 <Grid item xs={12} sm={6} md={3}>
    <Card
      sx={{
        p: { xs: 1.35, sm: 1.7, md: 2.2 },
        borderRadius: { xs: 2.5, md: 3.5 },
        minHeight: { xs: 112, md: 132 },
        height: "100%",
        border: "1px solid rgba(226,232,240,0.9)",
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,250,252,0.92))",
        boxShadow: "0 10px 25px rgba(15,23,42,0.07)",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="subtitle2"
        color="textSecondary"
        sx={{ fontWeight: 800, fontSize: { xs: "0.76rem", md: "0.9rem" } }}
      >
        🧾 {TT.totalOrders}
      </Typography>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 950,
          fontSize: { xs: 22, sm: 26, md: 34 },
          lineHeight: 1.08,
          wordBreak: "break-word",
          mt: 0.7,
        }}
      >
        {statsData.reduce(
          (sum, item) => sum + Number(item.siparis_sayisi || 0),
          0
        )}
      </Typography>
    </Card>
  </Grid>

 <Grid item xs={12} sm={6} md={3}>
    <Card
      sx={{
        p: { xs: 1.35, sm: 1.7, md: 2.2 },
        borderRadius: { xs: 2.5, md: 3.5 },
        minHeight: { xs: 112, md: 132 },
        height: "100%",
        border: "1px solid rgba(226,232,240,0.9)",
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,250,252,0.92))",
        boxShadow: "0 10px 25px rgba(15,23,42,0.07)",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="subtitle2"
        color="textSecondary"
        sx={{ fontWeight: 800, fontSize: { xs: "0.76rem", md: "0.9rem" } }}
      >
        📅 {TT.period}
      </Typography>

      <ToggleButtonGroup
        size="small"
        value={statsPeriod}
        exclusive
        onChange={(_, val) => val && setStatsPeriod(val)}
        sx={{
          mt: 1.2,
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(2,minmax(0,1fr))",
          gap: 0.8,
          "& .MuiToggleButtonGroup-grouped": {
            m: "0 !important",
            border: "1px solid rgba(203,213,225,0.9) !important",
          },
          "& .MuiToggleButton-root": {
            borderRadius: "12px !important",
            textTransform: "none",
            fontWeight: 900,
            minHeight: { xs: 38, md: 42 },
            fontSize: { xs: "0.74rem", md: "0.84rem" },
            whiteSpace: "nowrap",
            bgcolor: "#fff",
          },
        }}
      >
        <ToggleButton value="day">{TT.daily}</ToggleButton>
        <ToggleButton value="month">{TT.monthly}</ToggleButton>
      </ToggleButtonGroup>
    </Card>
  </Grid>
</Grid>

{/* Ürün Satış Tablosu */}
         <Card
  sx={{
    mb: { xs: 1.5, md: 3 },
    borderRadius: { xs: 3, md: 4 },
    overflow: "hidden",
    border: "1px solid rgba(226,232,240,0.9)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.07)",
    background: "#fff",
  }}
>
  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>📊 {TT.topProducts}</Typography>
              {statsLoading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : productSales.length === 0 ? (
                <Typography color="textSecondary">{TT.noSalesData}</Typography>
              ) : (
 


<Stack spacing={0.9}>
  {productSales.map((p, idx) => (
    <Box
      key={idx}
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) auto",
        alignItems: "center",
        gap: { xs: 1, md: 1.5 },
        border: "1px solid rgba(226,232,240,0.8)",
        bgcolor: "rgba(248,250,252,0.75)",
        borderRadius: 2.5,
        py: { xs: 1, md: 1.2 },
        px: { xs: 1, md: 1.4 },
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 800,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: { xs: "0.9rem", md: "1rem" },
            lineHeight: 1.2,
            mb: 0.2,
          }}
        >
          {p.urun_adi}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.78rem",
            fontWeight: 700,
          }}
        >
          {p.toplam_adet} {TT.piece}
        </Typography>
      </Box>

      <Typography
        variant="body1"
        sx={{
          fontWeight: 900,
          color: "primary.main",
          minWidth: { xs: 82, md: 110 },
          whiteSpace: "nowrap",
          fontSize: { xs: "0.9rem", md: "1.05rem" },
          textAlign: "right",
        }}
      >
        {Number(p.toplam_gelir).toFixed(2)} TL
      </Typography>
    </Box>
  ))}
</Stack>




              )}
            </CardContent>
                  </Card>







{/* ✅ STOK YÖNETİMİ */}
<Card
  sx={{
    ...mobileFullBleedSx,
    mb: { xs: 1.5, md: 3 },
    borderRadius: { xs: 3.5, md: 4.5 },

   overflow: "hidden",
    position: "relative",
   

 border: "1px solid rgba(226,232,240,0.95)",
    boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",

    "&::before": {
      content: '""',
      position: "absolute",
      inset: "0 0 auto 0",
      height: 5,
      background: "linear-gradient(90deg, #0b4d8b, #2563eb, #f58220)",
    },

    "&::after": {
      content: '""',
      position: "absolute",
      right: { xs: -80, md: -55 },
      top: { xs: -80, md: -55 },
      width: { xs: 160, md: 190 },
      height: { xs: 160, md: 190 },
      borderRadius: "50%",
      background: "rgba(37,99,235,0.08)",
      pointerEvents: "none",
    },
  }}
>
  <CardContent sx={{ p: { xs: 1.4, sm: 2, md: 2.7 }, pt: { xs: 2.2, md: 3.2 } }}>
    <Stack
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", md: "center" }}
      spacing={1.6}
      sx={{ mb: 2.2, position: "relative", zIndex: 1 }}
    >
      <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: { xs: 46, md: 54 },
            height: { xs: 46, md: 54 },
            borderRadius: 3,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #0b4d8b, #2563eb)",
            color: "#fff",
            boxShadow: "0 14px 30px rgba(37,99,235,0.24)",
          }}
        >
          <InventoryIcon />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 950,
              letterSpacing: -0.4,
              lineHeight: 1.12,
              fontSize: { xs: "1.18rem", md: "1.35rem" },
              color: "#0f172a",
            }}
          >
            📦 {TT.stockManagement}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.6,
              color: "text.secondary",
              fontWeight: 650,
              lineHeight: 1.5,
              fontSize: { xs: "0.88rem", md: "0.95rem" },
            }}
          >
            {TT.stockHelp}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{
          width: { xs: "100%", md: "auto" },
          minWidth: 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Chip
          label={`${stocks.length} ${TT.record || ""}`}
          sx={{
            height: 46,
            borderRadius: 999,
            px: 1,
            fontWeight: 950,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: "primary.dark",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
            display: { xs: "none", sm: "inline-flex" },
          }}
        />

        <Button
          variant="contained"
          startIcon={stockLoading ? <CircularProgress size={18} color="inherit" /> : <InventoryIcon />}
          onClick={() => {
            setStocks((prev) => [
              {
                id: `new-${Date.now()}`,
                title: "",
                stok_adedi: 0,
                minimum_stok: 0,
                manuel: true,
              },
              ...prev,
            ]);
          }}
          sx={{
            width: { xs: "100%", md: "auto" },
            minHeight: { xs: 52, md: 50 },
            borderRadius: 3,
            px: { xs: 2, md: 2.7 },
            textTransform: "none",
            fontWeight: 950,
            whiteSpace: "nowrap",
            background: "linear-gradient(135deg, #1976d2, #0b63ce)",
            boxShadow: "0 14px 30px rgba(25,118,210,0.26)",
            "&:hover": {
              background: "linear-gradient(135deg, #1565c0, #0b4d8b)",
              boxShadow: "0 18px 38px rgba(25,118,210,0.32)",
              transform: { xs: "none", md: "translateY(-1px)" },
            },
          }}
        >
          {TT.addStock}
        </Button>
      </Stack>
    </Stack>

    <Stack spacing={1.5} sx={{ position: "relative", zIndex: 1 }}>
      {stocks.length === 0 && (
        <Alert
          severity="info"
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
            bgcolor: alpha(theme.palette.info.main, 0.06),
            fontWeight: 750,
          }}
        >
          {TT.stockEmptyLong}
        </Alert>
      )}

      {stocks.map((item) => {
        const stockQty = Number(item.stok_adedi || 0);
        const minQty = Number(item.minimum_stok || 0);
        const isCritical = stockQty <= minQty;
        const fillPercent =
          minQty > 0 ? Math.max(0, Math.min(100, (stockQty / minQty) * 100)) : 100;

        return (
          <Card
            key={item.id}
            variant="outlined"
            sx={{
              borderRadius: { xs: 3, md: 3.5 },
              overflow: "hidden",
              border: `1px solid ${
                isCritical
                  ? alpha(theme.palette.error.main, 0.34)
                  : alpha(theme.palette.success.main, 0.22)
              }`,
              bgcolor: isCritical
                ? alpha(theme.palette.error.main, 0.035)
                : alpha(theme.palette.success.main, 0.025),
              boxShadow: isCritical
                ? "0 14px 34px rgba(239,68,68,0.10)"
                : "0 12px 30px rgba(15,23,42,0.055)",
            }}
          >
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.5, md: 1.8 },
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.72))",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.3}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                sx={{ mb: 1.4 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2.5,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isCritical
                        ? alpha(theme.palette.error.main, 0.11)
                        : alpha(theme.palette.success.main, 0.11),
                      color: isCritical ? "error.main" : "success.main",
                    }}
                  >
                    {isCritical ? <WarningAmberIcon /> : <CheckCircleIcon />}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 950,
                        color: "#0f172a",
                        lineHeight: 1.15,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.title || item.urun_adi || TT.productName}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 800,
                      }}
                    >
                      {TT.stockQty}: {stockQty} / {TT.minWarning}: {minQty}
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  icon={isCritical ? <WarningAmberIcon /> : <CheckCircleIcon />}
                  label={isCritical ? TT.critical : TT.stockNormal}
                  color={isCritical ? "error" : "success"}
                  sx={{
                    width: { xs: "100%", md: "auto" },
                    minHeight: 38,
                    borderRadius: 999,
                    fontWeight: 950,
                    justifyContent: "center",
                    boxShadow: isCritical
                      ? "0 10px 24px rgba(239,68,68,0.16)"
                      : "0 10px 24px rgba(16,185,129,0.13)",
                    "& .MuiChip-label": {
                      px: 1,
                      whiteSpace: "normal",
                      lineHeight: 1.15,
                    },
                  }}
                />
              </Stack>

              <Box
                sx={{
                  height: 8,
                  borderRadius: 999,
                  overflow: "hidden",
                  bgcolor: alpha(theme.palette.grey[500], 0.14),
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: `${fillPercent}%`,
                    height: "100%",
                    borderRadius: 999,
                    bgcolor: isCritical ? "error.main" : "success.main",
                    transition: "width 0.25s ease",
                  }}
                />
              </Box>

       

              <Box
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "minmax(260px, 2fr) minmax(140px, 0.85fr) minmax(150px, 0.9fr) minmax(230px, 1fr)",
                  },
                  gap: { xs: 1.1, sm: 1.2, md: 1.35 },
                  alignItems: "stretch",
                  overflow: "visible",
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  label={TT.productName}
                  placeholder="Örn: Ayran"
                  size="small"
                  value={item.title || item.urun_adi || ""}
                  onChange={(e) => {
                    setStocks((prev) =>
                      prev.map((s) =>
                        s.id === item.id
                          ? {
                              ...s,
                              title: e.target.value,
                              urun_adi: e.target.value,
                            }
                          : s
                      )
                    );
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,

                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      maxWidth: "100%",
                      minHeight: { xs: 54, md: 58 },
                      borderRadius: 2.5,
                      bgcolor: "#fff",
                      alignItems: "center",
                    },

                    "& .MuiInputBase-input": {
                      fontWeight: 900,
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      py: { xs: 1.35, md: 1.45 },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },

                    "& .MuiInputLabel-root": {
                      fontWeight: 800,
                      bgcolor: "#fff",
                      px: 0.4,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label={TT.stockQty}
                  placeholder="100"
                  size="small"
                  type="number"
                  value={item.stok_adedi ?? ""}
                  onChange={(e) => {
                    setStocks((prev) =>
                      prev.map((s) =>
                        s.id === item.id
                          ? { ...s, stok_adedi: e.target.value }
                          : s
                      )
                    );
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,

                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      maxWidth: "100%",
                      minHeight: { xs: 54, md: 58 },
                      borderRadius: 2.5,
                      bgcolor: "#fff",
                      alignItems: "center",
                    },

                    "& .MuiInputBase-input": {
                      fontWeight: 900,
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      py: { xs: 1.35, md: 1.45 },
                    },

                    "& .MuiInputLabel-root": {
                      fontWeight: 800,
                      bgcolor: "#fff",
                      px: 0.4,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label={TT.minWarning}
                  placeholder="10"
                  size="small"
                  type="number"
                  value={item.minimum_stok ?? ""}
                  onChange={(e) => {
                    setStocks((prev) =>
                      prev.map((s) =>
                        s.id === item.id
                          ? { ...s, minimum_stok: e.target.value }
                          : s
                      )
                    );
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,

                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      maxWidth: "100%",
                      minHeight: { xs: 54, md: 58 },
                      borderRadius: 2.5,
                      bgcolor: "#fff",
                      alignItems: "center",
                    },

                    "& .MuiInputBase-input": {
                      fontWeight: 900,
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      py: { xs: 1.35, md: 1.45 },
                    },

                    "& .MuiInputLabel-root": {
                      fontWeight: 800,
                      bgcolor: "#fff",
                      px: 0.4,
                    },
                  }}
                />

                <Stack
                  direction="row"
                  spacing={1.1}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    height: "100%",
                    alignItems: "stretch",

                    gridColumn: {
                      xs: "1 / -1",
                      sm: "1 / -1",
                      md: "auto",
                    },

                    "& .MuiButton-root": {
                      flex: 1,
                      width: "100%",
                      minWidth: 0,
                      minHeight: { xs: 52, md: 58 },
                      px: { xs: 1.5, md: 2 },
                      borderRadius: 2.6,
                      textTransform: "none",
                      fontWeight: 950,
                      whiteSpace: "nowrap",
                      fontSize: { xs: "0.94rem", md: "0.98rem" },
                    },
                  }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={async () => {
                      try {
                        await saveStock(item);
                      } catch (err) {
                        setSnackbar({
                          open: true,
                          message: err.message || TT.stockSaveFail,
                          severity: "error",
                        });
                      }
                    }}
                    sx={{
                      background: "linear-gradient(135deg, #1976d2, #0b63ce)",
                      boxShadow: "0 10px 22px rgba(25,118,210,0.18)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #1565c0, #0b4d8b)",
                      },
                    }}
                  >
                    {TT.save}
                  </Button>

                  <Button
                    fullWidth
                    color="error"
                    variant="outlined"
                    size="small"
                    onClick={async () => {
                      try {
                        await deleteStock(item);
                      } catch (err) {
                        setSnackbar({
                          open: true,
                          message: err.message || TT.stockDeleteFail,
                          severity: "error",
                        });
                      }
                    }}
                    sx={{
                      borderWidth: 2,
                      bgcolor: "#fff",
                      "&:hover": {
                        borderWidth: 2,
                        bgcolor: alpha(theme.palette.error.main, 0.04),
                      },
                    }}
                  >
                    {TT.delete}
                  </Button>
                </Stack>
              </Box>

              <Grid item xs={12}>
  <TextField
    fullWidth
    label={TT.stockQty}
                    placeholder="100"
                    size="small"
                    type="number"
                    value={item.stok_adedi ?? ""}
                    onChange={(e) => {
                      setStocks((prev) =>
                        prev.map((s) =>
                          s.id === item.id
                            ? { ...s, stok_adedi: e.target.value }
                            : s
                        )
                      );
                    }}
                    sx={{
  width: "100%",
  maxWidth: "100%",

  "& .MuiOutlinedInput-root": {
    width: "100%",
    maxWidth: "100%",
    borderRadius: 2.5,
    bgcolor: "#fff",
    fontWeight: 850,
    minHeight: 54,
  },

  "& .MuiInputBase-input": {
    fontWeight: 850,
  },

  "& .MuiInputLabel-root": {
    fontWeight: 750,
  },
}}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={TT.minWarning}
                    placeholder="10"
                    size="small"
                    type="number"
                    value={item.minimum_stok ?? ""}
                    onChange={(e) => {
                      setStocks((prev) =>
                        prev.map((s) =>
                          s.id === item.id
                            ? { ...s, minimum_stok: e.target.value }
                            : s
                        )
                      );
                    }}
                    sx={{
  width: "100%",
  maxWidth: "100%",

  "& .MuiOutlinedInput-root": {
    width: "100%",
    maxWidth: "100%",
    borderRadius: 2.5,
    bgcolor: "#fff",
    fontWeight: 850,
    minHeight: 54,
  },

  "& .MuiInputBase-input": {
    fontWeight: 850,
  },

  "& .MuiInputLabel-root": {
    fontWeight: 750,
  },
}}
                  />
                </Grid>

<Grid item xs={12} md={4}>

  <Stack
    direction="row"
    spacing={1.5}
    sx={{
      width: "100%",
      maxWidth: "100%",
      height: "100%",
      mt: { xs: 0.4, md: 0 },
      alignItems: "stretch",

      "& .MuiButton-root": {
        flex: 1,
        width: "100%",
        minWidth: 0,
        minHeight: { xs: 58, md: 74 },
        px: { xs: 2, md: 3 },
        borderRadius: 2.8,
        textTransform: "none",
        fontWeight: 950,
        whiteSpace: "nowrap",
        fontSize: { xs: "0.95rem", md: "1.08rem" },
      },
    }}
  >                   <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={async () => {
                        try {
                          await saveStock(item);
                        } catch (err) {
                          setSnackbar({
                            open: true,
                            message: err.message || TT.stockSaveFail,
                            severity: "error",
                          });
                        }
                      }}
                      sx={{
                        background: "linear-gradient(135deg, #1976d2, #0b63ce)",
                        boxShadow: "0 12px 24px rgba(25,118,210,0.20)",
                        "&:hover": {
                          background: "linear-gradient(135deg, #1565c0, #0b4d8b)",
                        },
                      }}
                    >
                      {TT.save}
                    </Button>

                    <Button
                      fullWidth
                      color="error"
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        try {
                          await deleteStock(item);
                        } catch (err) {
                          setSnackbar({
                            open: true,
                            message: err.message || TT.stockDeleteFail,
                            severity: "error",
                          });
                        }
                      }}
                      sx={{
                        borderWidth: 2,
                        bgcolor: "#fff",
                        "&:hover": {
                          borderWidth: 2,
                          bgcolor: alpha(theme.palette.error.main, 0.04),
                        },
                      }}
                    >
                      {TT.delete}
                    </Button>
                  </Stack>
                </Grid>
              </Box>
          </Card>
        );
      })}
    </Stack>
  </CardContent>
</Card>

   
{/* Zaman Serisi */}
<Card
  sx={{
    ...mobileFullBleedSx,
    mb: { xs: 1.5, md: 3 },
    borderRadius: { xs: 3, md: 4 },

   overflow: "hidden",


    border: "1px solid rgba(226,232,240,0.9)",
    boxShadow: "0 12px 30px rgba(15,23,42,0.07)",
    background: "#fff",
  }}
>
  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 900,
        mb: 2,
        fontSize: { xs: "1rem", md: "1.18rem" },
      }}
    >
      📈{" "}
      {TT.revenueTrendTitle.replace(
        "{period}",
        statsPeriod === "day" ? TT.dailyTrend : TT.monthlyTrend
      )}
    </Typography>

    <Box
      sx={{
        overflowX: "auto",
        width: "100%",
        maxWidth: "100%",
        borderRadius: 2.5,
        border: "1px solid rgba(226,232,240,0.9)",
        WebkitOverflowScrolling: "touch",
      }}
    >
   <table
  style={{
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "430px",
  }}
>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "10px" }}>
              {TT.periodCol}
            </th>
            <th style={{ textAlign: "left", padding: "10px" }}>
              {TT.orderCol}
            </th>
            <th style={{ textAlign: "left", padding: "10px" }}>
              {TT.revenueWithCurrency}
            </th>
          </tr>
        </thead>

        <tbody>
          {statsData.map((item, idx) => (
            <tr key={idx} style={{ borderBottom: "1px solid #e5e7eb" }}>
              <td style={{ padding: "10px", wordBreak: "break-word" }}>
                {statsPeriod === "day"
                  ? new Date(item.date).toLocaleDateString(getLocale(lang))
                  : item.date_trunc
                    ? new Date(item.date_trunc).toLocaleDateString(getLocale(lang))
                    : "-"}
              </td>

              <td style={{ padding: "10px", fontWeight: 700 }}>
                {item.siparis_sayisi}
              </td>

              <td
                style={{
                  padding: "10px",
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                  color: "#0b4d8b",
                }}
              >
                {Number(item.ciro || 0).toFixed(2)} TL
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  </CardContent>
</Card>
{/* Orders List */}
{paginatedOrders.length === 0 && !loading ? (
  <Card
    sx={{
      borderRadius: { xs: 3, md: 4 },
      textAlign: "center",
      py: { xs: 4, md: 6 },
      px: 2,
      border: "1px solid rgba(226,232,240,0.9)",
      background: alpha(theme.palette.background.paper, 0.82),
      boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
    }}
  >
    <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 800 }}>
      {searchTerm || filterStatus !== "all"
        ? `🔍 ${TT.noFiltered}`
        : `📭 ${TT.noOrders}`}
    </Typography>
  </Card>
) : (
  <Stack
    spacing={{ xs: 1.5, md: 2.5 }}
    sx={{
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      overflowX: "hidden",
    }}
  >
    {paginatedOrders.map((order) => (
     <OrderCard
  key={order.id}
  order={order}
  onStatusChange={handleStatusUpdate}
  onCancelClick={handleCancelClick}
  onDeleteClick={deleteOrderPermanently}
        statusColor={statusColor}
        statusLabel={statusLabel}
        formatDate={formatDate}
        TT={TT}
        lang={lang}
      />
    ))}
  </Stack>
)}

{/* Pagination */}
{pageCount > 1 && (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      mt: { xs: 2.5, md: 4 },
      width: "100%",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      pb: 1,
    }}
  >
    <Pagination
      count={pageCount}
      page={page}
      onChange={(_, value) => setPage(value)}
      color="primary"
      size="large"
      sx={{
        "& .MuiPagination-ul": {
          flexWrap: "nowrap",
        },
        "& .MuiPaginationItem-root": {
          borderRadius: 2,
          fontWeight: 800,
          minWidth: { xs: 34, md: 40 },
          height: { xs: 34, md: 40 },
        },
      }}
    />
  </Box>
)}

</Box>



      {/* Garson Çağrı Dialog */}
<Dialog
  open={showWaiterDialog}
  onClose={() => setShowWaiterDialog(false)}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: { xs: 3, md: 4 },
      m: { xs: 1.5, sm: 3 },
      width: { xs: "calc(100% - 24px)", sm: "100%" },
      overflow: "hidden",
    },
  }}
>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
<Typography>🔔 {TT.waiterCalls}</Typography>
            <IconButton onClick={() => setShowWaiterDialog(false)}>
              <CancelIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {waiterCalls.length === 0 ? (
            <Typography color="textSecondary" sx={{ py: 4, textAlign: "center" }}>
             {TT.noWaiter}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {waiterCalls.map((call) => (
                <Card
                  key={call.id}
                  sx={{
                    p: 2,
                    bgcolor: call.is_read ? "background.paper" : alpha(theme.palette.warning.main, 0.1),
                    borderLeft: `4px solid ${call.is_read ? theme.palette.grey[400] : theme.palette.warning.main}`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                      {TT.table} #{call.masa_no || TT.tableUnspecified}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                       {call.musteri_adi || TT.guest}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(call.created_at)}
                      </Typography>
                    </Box>
                    {!call.is_read && (
                      <Button size="small" variant="contained" color="primary" onClick={() => markWaiterCallAsRead(call.id)}>
                       {TT.markRead}
                      </Button>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
        <Button onClick={() => setShowWaiterDialog(false)}>{TT.close}</Button>
        </DialogActions>
      </Dialog>


      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 4, p: 1 },
        }}
      >
       <DialogTitle sx={{ fontWeight: 700 }}>{TT.cancelTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
          {TT.cancelText}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2 }}>
          <Button onClick={() => setCancelDialogOpen(false)} variant="outlined">
{TT.giveUp}
          </Button>
          <Button onClick={confirmCancel} color="error" variant="contained">
{TT.cancelOrder}
          </Button>
        </DialogActions>
      </Dialog>

          {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ mb: { xs: 2, sm: 3 } }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 3 }}
          iconMapping={{
            success: <CheckCircleIcon fontSize="inherit" />,
            error: <CancelIcon fontSize="inherit" />,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

</ErrorBoundary>
  );
}